import * as React from "react"
import { cn } from "@/lib/utils"
import { Skeleton } from "@/components/ui/skeleton"

interface DataTableSkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  columns?: number
  rows?: number
  showHeader?: boolean
}

const DataTableSkeleton = React.forwardRef<HTMLDivElement, DataTableSkeletonProps>(
  ({ className, columns = 5, rows = 5, showHeader = true, ...props }, ref) => {
    return (
      <div ref={ref} className={cn("w-full", className)} {...props}>
        <div className="rounded-lg border">
          {showHeader && (
            <div className="border-b bg-muted/50 p-4">
              <div className="flex items-center gap-4">
                {Array.from({ length: columns }).map((_, i) => (
                  <Skeleton key={i} className="h-4 min-w-6 flex-1" />
                ))}
              </div>
            </div>
          )}
          <div className="divide-y">
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <div key={rowIndex} className="p-4">
                <div className="flex items-center gap-4">
                  {Array.from({ length: columns }).map((_, colIndex) => (
                    <Skeleton
                      key={colIndex}
                      className={cn(
                        "h-4 min-w-6 flex-1",
                        colIndex === 0 && "max-w-[200px]"
                      )}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }
)
DataTableSkeleton.displayName = "DataTableSkeleton"

interface CardGridSkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  cards?: number
}

const CardGridSkeleton = React.forwardRef<HTMLDivElement, CardGridSkeletonProps>(
  ({ className, cards = 4, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("grid gap-4 md:grid-cols-2 lg:grid-cols-4", className)}
        {...props}
      >
        {Array.from({ length: cards }).map((_, i) => (
          <div key={i} className="rounded-lg border p-6 space-y-3">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-16" />
            <Skeleton className="h-3 w-20" />
          </div>
        ))}
      </div>
    )
  }
)
CardGridSkeleton.displayName = "CardGridSkeleton"

interface ListSkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  items?: number
}

const ListSkeleton = React.forwardRef<HTMLDivElement, ListSkeletonProps>(
  ({ className, items = 5, ...props }, ref) => {
    return (
      <div ref={ref} className={cn("space-y-3", className)} {...props}>
        {Array.from({ length: items }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 rounded-lg border p-4">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="h-6 w-16" />
          </div>
        ))}
      </div>
    )
  }
)
ListSkeleton.displayName = "ListSkeleton"

export { DataTableSkeleton, CardGridSkeleton, ListSkeleton }
